import { useQueryClient } from '@tanstack/react-query'
import to from 'await-to-js'
import CreatableSelect from 'components/FormUtils/CreatableSelect'
import { Control, FieldValues, Path, UseFormSetError } from 'react-hook-form'
import useApiService from 'services/api'
import { handleFormError } from 'utils/handleFormError'
import { toast } from 'react-toastify'
import { MULTI_VALUE_SELECT_COLORS } from 'utils/constants'
import { Tag } from 'models/tags'
import { colors } from '../../styles/tokens'
import { ds2026SelectStyles } from '../FbUI/Ds2026Field'

export function CreatableTagSelector<T extends FieldValues>({
  name,
  control,
  options,
  setError,
  label,
  placeholder,
  tagParent = 'products',
  className,
  optional,
  labelClassName,
  whiteBackground = false,
  ds2026 = false,
  hideColorDot = false,
}: {
  name: Path<T>

  control: Control<T, any>

  options: any
  setError: UseFormSetError<T>
  label?: string
  placeholder?: string
  tagParent?: 'products' | 'contacts' | 'documents' | 'deals'
  className?: string
  optional?: boolean
  labelClassName?: string
  whiteBackground?: boolean
  /** Apply the DS2026 gray (neutral-100) menu hover instead of the default. */
  ds2026?: boolean
  /** Hide the per-option color swatch — use when the tags have no colors
   *  (e.g. contact labels), so the menu isn't littered with identical gray dots. */
  hideColorDot?: boolean
}) {
  const api = useApiService()
  const queryClient = useQueryClient()
  const { data: tags } = api.useGetTags(tagParent)

  const onCreateOption = async (value: string) => {
    if (value.length > 50) {
      toast.error('Tag length limited to 50 characters.')
      return null
    }

    // For documents, tags are created automatically during upload
    // So we just return the string value for selection
    if (tagParent === 'documents') {
      return {
        label: value,
        value: value, // Use string value for documents
      }
    }

    const [err, res] = await to(
      api.postTag(
        {
          tag: value,
        },
        tagParent
      )
    )

    if (err) handleFormError(err, setError)
    else {
      // Invalidate tags query
      queryClient.invalidateQueries({ queryKey: ['tags', tagParent] })
      return {
        label: res?.tag,
        value: res?.id,
      }
    }

    return null
  }

  const getTagColor = (tagValue: any) => {
    // For documents, tagValue is a string (tag name)
    // For contacts/products, tagValue is a number (tag ID)
    const tag = tags?.find((t: Tag) =>
      tagParent === 'documents' ? t.tag === tagValue : t.id === tagValue
    )
    if (tag?.color) {
      return (
        MULTI_VALUE_SELECT_COLORS.find((c) => c.background === tag.color) ||
        MULTI_VALUE_SELECT_COLORS[0]
      )
    }
    return MULTI_VALUE_SELECT_COLORS[0]
  }

  // In DS2026 mode, adopt the shared DS2026 control + caret/clear styling so the
  // tag picker matches the DsInput fields. The per-tag color chips and the
  // swatch-prefixed option (below) stay; only the shell/indicators are themed.
  const ds2026ControlStyles = ds2026
    ? {
        control: ds2026SelectStyles.control,
        valueContainer: ds2026SelectStyles.valueContainer,
        // flexGrow 0 so the type-ahead input doesn't split the row width with
        // the chips — the chips' FittedPillRow gets the full space and only
        // collapses to "+N" when the chips genuinely don't fit.
        input: (base: any) => ({
          ...ds2026SelectStyles.input(base),
          flexGrow: 0,
        }),
        placeholder: ds2026SelectStyles.placeholder,
        dropdownIndicator: ds2026SelectStyles.dropdownIndicator,
        clearIndicator: ds2026SelectStyles.clearIndicator,
        indicatorSeparator: ds2026SelectStyles.indicatorSeparator,
        menu: ds2026SelectStyles.menu,
        menuList: ds2026SelectStyles.menuList,
      }
    : {}

  const customStyles = {
    ...(whiteBackground && {
      control: (provided: any) => ({
        ...provided,
        backgroundColor: 'white',
      }),
    }),
    ...ds2026ControlStyles,
    multiValue: (provided: any, state: any) => {
      // DS2026: default-gray rounded chip (the wrapper already sets radius 6px).
      if (ds2026) {
        return {
          ...provided,
          backgroundColor: colors['neutral-100'],
          color: colors['neutral-700'],
        }
      }
      const color = getTagColor(state.data.value)
      return {
        ...provided,
        backgroundColor: color.background,
        color: color.text,
      }
    },
    multiValueLabel: (provided: any, state: any) => {
      if (ds2026) {
        return { ...provided, color: colors['neutral-700'], fontSize: 12 }
      }
      const color = getTagColor(state.data.value)
      return {
        ...provided,
        color: color.text,
      }
    },
    multiValueRemove: (provided: any, state: any) => {
      if (ds2026) {
        return {
          ...provided,
          color: colors['neutral-500'],
          ':hover': {
            backgroundColor: colors['neutral-200'],
            color: colors['neutral-700'],
          },
        }
      }
      const color = getTagColor(state.data.value)
      return {
        ...provided,
        color: color.text,
        ':hover': {
          backgroundColor: color.background,
          color: color.text,
        },
      }
    },
    option: (provided: any, state: any) => {
      const color = getTagColor(state.data.value)
      return {
        ...provided,
        display: 'flex',
        alignItems: 'center',
        ...(hideColorDot ? {} : { gap: '8px' }),
        // DS2026: neutral (gray-100) hover/selected fill, matching the other
        // DS2026 dropdowns. Overrides react-select's default green primary25.
        ...(ds2026 && {
          cursor: 'pointer',
          borderRadius: 6, // rounded inset hover row
          backgroundColor:
            state.isFocused || state.isSelected
              ? colors['neutral-100']
              : 'transparent',
          color:
            state.isFocused || state.isSelected
              ? colors['neutral-900']
              : colors['neutral-700'],
          ':active': { backgroundColor: colors['neutral-100'] },
        }),
        ...(hideColorDot
          ? {}
          : {
              ':before': {
                content: '""',
                display: 'inline-block',
                width: '12px',
                height: '12px',
                borderRadius: '3px',
                backgroundColor: color.background,
                flexShrink: 0,
              },
            }),
      }
    },
  }

  return (
    <CreatableSelect
      name={name}
      isMulti
      control={control}
      type="text"
      label={label}
      placeholder={placeholder}
      options={options}
      onCreateOption={onCreateOption}
      className={className}
      optional={optional}
      styles={customStyles}
      ds2026={ds2026}
      labelClassName={labelClassName}
    />
  )
}
